import { TFunction } from 'next-i18next';
import { CountryCode } from 'types/countries';
import { LOCALE_SEARCH_PATHS } from 'types/locales';
import { SearchOptions, SEARCH_OPTION_AGENTS, SEARCH_OPTION_HOME_APPRAISAL } from '@tenants/types/theme';
import { translate } from 'utils/translations';

interface CreateSearchPlaceholderOptions {
  /** Translation function from useTranslation(Namespaces.SEARCH) */
  t: TFunction;
  /** The search option type (Buy, Rent, Sold, Agents, HomeAppraisal) */
  searchOption: SearchOptions;
  /** Country code for regional variations (neighbourhood vs neighborhood) */
  siteLocation?: CountryCode;
  /** Whether to show mobile-optimized (shorter) placeholder */
  isMobile?: boolean;
}

/**
 * Creates internationalized search placeholders using the search namespace.
 * This function provides centralized placeholder generation for all search components.
 * 
 * @param options - Configuration object for placeholder generation
 * @returns Internationalized placeholder string
 */
export function createLocalizedSearchPlaceholder(options: CreateSearchPlaceholderOptions): string {
  const { t, searchOption, siteLocation, isMobile } = options;
  // Get the localized neighbourhood/neighborhood term
  const neighbourhood = translate('neighbourhood', siteLocation);

  switch (searchOption) {
    case SEARCH_OPTION_AGENTS:
      return t(LOCALE_SEARCH_PATHS.PLACEHOLDER_AGENTS_DESKTOP, {
        defaultValue: 'Enter a city or {{neighbourhood}}',
        neighbourhood,
      });

    case SEARCH_OPTION_HOME_APPRAISAL:
      return t(LOCALE_SEARCH_PATHS.PLACEHOLDER_HOME_APPRAISAL, {
        defaultValue: 'Enter your home address',
      });

    default: // Buy, Rent, Sold
      if (isMobile) {
        return t(LOCALE_SEARCH_PATHS.PLACEHOLDER_BUY_RENT_SOLD_MOBILE, {
          defaultValue: 'City, {{neighbourhood}}, or address',
          neighbourhood,
        });
      } else {
        return t(LOCALE_SEARCH_PATHS.PLACEHOLDER_BUY_RENT_SOLD_DESKTOP, {
          defaultValue: 'Enter a city, {{neighbourhood}}, address, MLS® number or school',
          neighbourhood,
        });
      }
  }
}
